import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search, Calendar, Package, AlertTriangle, Clock, ChevronLeft, ChevronRight, CheckCircle2, BarChart3, DollarSign, Plane, Send, Flame, ChevronUp, ChevronDown, Download } from 'lucide-react';
import * as XLSXStyle from 'xlsx-js-style';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { usePageFocus } from '@/hooks/usePageFocus';
import { parseExpiryDate, getDaysDifference } from '@/lib/skuUtils';
import { triggerReloadSearch, pickReloadSearch } from '@/utils/reloadSearch';
import { decodeHtmlEntities } from '@/lib/utils';

const EXPIRY_CATEGORY: CategoryKey = 'expiry';

interface ExpiryProduct {
  sku_id: string;
  brand: string;
  sku_name: string;
  sku_name_chi: string;
  sku_name_sc: string;
  quantity: number;
  days_remaining: number;
  cost: number;
  expiry_date: Date;
  sku_status: 'online' | 'offline';
}

interface StatisticsData {
  totalProducts: number;
  expiredProducts: number; // 已过期产品
  urgentProducts: number; // 7天内到期
  nearExpiryProducts: number; // 30天内到期
  mediumExpiryProducts: number; // 90天内到期
  longExpiryProducts: number; // 180天内到期
}

// 分批取數輔助函式（避免 Supabase 單次 1000 筆限制）
const fetchAllData = async <T,>(
  query: any,
  batchSize: number = 1000
): Promise<T[]> => {
  let all: T[] = [];
  let start = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await query.range(start, start + batchSize - 1);
    if (error) throw error;

    if (data && data.length > 0) {
      all = all.concat(data as T[]);
      start += batchSize;
      hasMore = data.length === batchSize;
    } else {
      hasMore = false;
    }
  }

  return all;
};

const ProductExpirySummary = () => {
  const [products, setProducts] = useState<ExpiryProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ExpiryProduct[]>([]);
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalProducts: 0,
    expiredProducts: 0,
    urgentProducts: 0,
    nearExpiryProducts: 0,
    mediumExpiryProducts: 0,
    longExpiryProducts: 0
  });
  const [loading, setLoading] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);

  // 跟踪加载开始时间，用于检测stale loading状态
  const loadingStartedAtRef = useRef<number | null>(null);

  // 跟踪stale loading恢复定时器
  const staleRecoveryTimerRef = useRef<number | null>(null);

  // 搜索和筛选状态
  const [daysRange, setDaysRange] = useState<'expired' | '30' | '90' | '180'>('180');
  const [productStatusFilter, setProductStatusFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [inventoryFilter, setInventoryFilter] = useState<'has_stock' | 'zero_stock'>('has_stock');
  const [searchSkuId, setSearchSkuId] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [searchName, setSearchName] = useState('');

  // 到期產品列表排序：品牌、天數（預設天數由小到大：-12 -> -1 -> 0 -> 2 -> 4 -> 12）
  type SortKey = 'brand' | 'days_remaining' | null;
  type SortDirection = 'asc' | 'desc' | null;
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'days_remaining',
    direction: 'asc'
  });

  const navigate = useNavigate();
  const { language } = useLanguage();

  // 获取当前语言的产品名称
  const getProductDisplayName = (product: ExpiryProduct): string => {
    let name: string;
    if (language === 'zh') {
      name = product.sku_name_chi || product.sku_name || product.sku_name_sc || '未命名產品';
    } else {
      name = product.sku_name || product.sku_name_chi || product.sku_name_sc || 'Unnamed Product';
    }
    return decodeHtmlEntities(name);
  };

  // 获取产品到期信息
  const getProductExpiryInfo = (product: ExpiryProduct): { date: string; status: 'urgent' | 'warning' | 'normal' } => {
    const days = product.days_remaining;

    if (days <= 7) {
      return { date: product.expiry_date.toLocaleDateString(), status: 'urgent' };
    } else if (days <= 30) {
      return { date: product.expiry_date.toLocaleDateString(), status: 'warning' };
    } else {
      return { date: product.expiry_date.toLocaleDateString(), status: 'normal' };
    }
  };

  // 加载数据
  const loadExpiryData = async (companyId: number, daysRange: 'expired' | '30' | '90' | '180') => {
    setLoading(true);
    loadingStartedAtRef.current = Date.now(); // 记录加载开始时间
    try {
      // Removed fallback queries on expiry summary data loads so products and inventory remain scoped to the current company.

      // 获取当前公司的产品数据（分批取數，避免 1000 筆上限）
      const productsQuery = supabase
        .from('products')
        .select(`
          sku_id,
          brand,
          sku_name,
          sku_name_chi,
          sku_name_sc,
          company_sku_key,
          sku_status
        `)
        .eq('company_id', companyId);

      const productsData = await fetchAllData<any>(productsQuery);

      // 获取库存数据（分批取數，避免 1000 筆上限）
      const inventoryQuery = supabase
        .from('product_inventory')
        .select('sku_id, merchant_inventory_qty, company_sku_key')
        .eq('company_id', companyId);

      const inventoryData = await fetchAllData<any>(inventoryQuery);

      // 严格限制：仅查询当前公司的成本数据，绝对不使用任何回退查询（同樣分批取數）
      const costQuery = supabase
        .from('product_costs')
        .select('company_sku_key, average_cost')
        .eq('company_id', companyId)
        .order('average_cost_date', { ascending: false });

      let costData: any[] | null = null;
      try {
        costData = await fetchAllData<any>(costQuery);
      } catch (costError) {
        // 记录警告但不抛出错误，因为成本数据不是必需的
        console.warn('獲取成本資料失敗，但繼續處理:', costError);
      }

      // 创建成本映射
      const costMap = new Map<string, number>();
      (costData || []).forEach(cost => {
        if (cost.company_sku_key && !costMap.has(cost.company_sku_key)) {
          costMap.set(cost.company_sku_key, cost.average_cost);
        }
      });

      // 处理产品数据
      const expiryProducts: ExpiryProduct[] = [];
      const stats: StatisticsData = {
        totalProducts: 0,
        expiredProducts: 0,
        urgentProducts: 0,
        nearExpiryProducts: 0,
        mediumExpiryProducts: 0,
        longExpiryProducts: 0
      };

      productsData.forEach(product => {
        // 从产品名称中解析到期日期
        const productNames = [product.sku_name, product.sku_name_chi, product.sku_name_sc].filter(Boolean);
        let expiryDate: Date | null = null;

        for (const name of productNames) {
          expiryDate = parseExpiryDate(name);
          if (expiryDate) break;
        }

        if (expiryDate) {
          const daysRemaining = getDaysDifference(expiryDate);

          // 根据选择的时间范围过滤产品
          let shouldInclude = false;
          if (daysRange === 'expired') {
            shouldInclude = daysRemaining < 0; // 已过期：小于0天
          } else {
            const days = parseInt(daysRange);
            shouldInclude = daysRemaining <= days; // 包含已过期：小于等于指定天数
          }

          if (shouldInclude) {
            // 获取库存数量
            const inventory = inventoryData.find((inv: any) =>
              inv.company_sku_key
                ? inv.company_sku_key === product.company_sku_key
                : inv.sku_id === product.sku_id
            );
            const quantity = inventory?.merchant_inventory_qty || 0;

            // 获取成本
            const cost = costMap.get(product.company_sku_key || '') || 0;

            const expiryProduct: ExpiryProduct = {
              sku_id: product.sku_id,
              brand: product.brand || '未分類',
              sku_name: product.sku_name || '',
              sku_name_chi: product.sku_name_chi || '',
              sku_name_sc: product.sku_name_sc || '',
              quantity,
              days_remaining: daysRemaining,
              cost,
              expiry_date: expiryDate,
              sku_status: (product.sku_status || '').toUpperCase() === 'ONLINE' ? 'online' : 'offline'
            };

            expiryProducts.push(expiryProduct);
            stats.totalProducts++;

            // 统计不同时间范围的产品数量
            if (daysRemaining < 0) {
              stats.expiredProducts++;
            } else {
              if (daysRemaining <= 7) stats.urgentProducts++;
              if (daysRemaining <= 30) stats.nearExpiryProducts++;
              if (daysRemaining <= 90) stats.mediumExpiryProducts++;
              if (daysRemaining <= 180) stats.longExpiryProducts++;
            }
          }
        }
      });

      setProducts(expiryProducts);
      setFilteredProducts(expiryProducts);
      setStatistics(stats);

    } catch (error: any) {
      console.error('加载到期数据失败:', error);
      toast.error(language === 'zh' ? '載入數據失敗' : 'Failed to load data');
    } finally {
      setLoading(false);
      loadingStartedAtRef.current = null; // 清除加载开始时间

      // 清除stale recovery定时器
      if (staleRecoveryTimerRef.current) {
        clearTimeout(staleRecoveryTimerRef.current);
        staleRecoveryTimerRef.current = null;
      }
    }
  };

  // 应用搜索和筛选
  const applyFilters = () => {
    let filtered = [...products];

    // 狀態過濾
    if (productStatusFilter !== 'all') {
      filtered = filtered.filter(product =>
        product.sku_status === productStatusFilter
      );
    }

    // 存貨量過濾
    if (inventoryFilter === 'has_stock') {
      filtered = filtered.filter(product => product.quantity > 0);
    } else if (inventoryFilter === 'zero_stock') {
      filtered = filtered.filter(product => product.quantity === 0);
    }

    // SKU ID 搜索
    if (searchSkuId.trim()) {
      filtered = filtered.filter(product =>
        product.sku_id.toLowerCase().includes(searchSkuId.toLowerCase())
      );
    }

    // 品牌搜索
    if (searchBrand.trim()) {
      filtered = filtered.filter(product =>
        product.brand.toLowerCase().includes(searchBrand.toLowerCase())
      );
    }

    // 名稱搜索（搜尋 sku_name, sku_name_chi, sku_name_sc）
    if (searchName.trim()) {
      const searchTerm = searchName.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.sku_name.toLowerCase().includes(searchTerm) ||
        product.sku_name_chi.toLowerCase().includes(searchTerm) ||
        product.sku_name_sc.toLowerCase().includes(searchTerm)
      );
    }

    // 重新計算篩選後的統計數據
    const filteredStats: StatisticsData = {
      totalProducts: 0,
      expiredProducts: 0,
      urgentProducts: 0,
      nearExpiryProducts: 0,
      mediumExpiryProducts: 0,
      longExpiryProducts: 0
    };

    filtered.forEach(product => {
      filteredStats.totalProducts++;

      if (product.days_remaining < 0) {
        filteredStats.expiredProducts++;
      } else {
        if (product.days_remaining <= 7) filteredStats.urgentProducts++;
        if (product.days_remaining <= 30) filteredStats.nearExpiryProducts++;
        if (product.days_remaining <= 90) filteredStats.mediumExpiryProducts++;
        if (product.days_remaining <= 180) filteredStats.longExpiryProducts++;
      }
    });

    setFilteredProducts(filtered);
    setStatistics(filteredStats);
  };

  // 监听筛选条件变化
  useEffect(() => {
    applyFilters();
  }, [productStatusFilter, inventoryFilter, searchSkuId, searchBrand, searchName, products]);

  // 依目前排序設定產生的顯示列表（天數預設由小到大）
  const displayProducts = useMemo(() => {
    const list = [...filteredProducts];
    const key = sortConfig.key ?? 'days_remaining';
    const dir = sortConfig.direction ?? 'asc';
    if (key === 'days_remaining') {
      list.sort((a, b) => (dir === 'asc' ? a.days_remaining - b.days_remaining : b.days_remaining - a.days_remaining));
    } else if (key === 'brand') {
      list.sort((a, b) => (dir === 'asc' ? a.brand.localeCompare(b.brand) : b.brand.localeCompare(a.brand)));
    }
    return list;
  }, [filteredProducts, sortConfig]);

  const handleSort = (key: 'brand' | 'days_remaining') => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
        return { key, direction: 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // 页面焦点恢复处理器
  const handlePageFocusRecovery = useCallback(async () => {
    const now = Date.now();
    const STALE_LOADING_THRESHOLD = 3000; // 3秒

    // 检查是否是stale loading状态
    if (loading && loadingStartedAtRef.current) {
      const loadingDuration = now - loadingStartedAtRef.current;
      if (loadingDuration > STALE_LOADING_THRESHOLD) {
        console.log(`🔄 檢測到stale loading狀態 (${loadingDuration}ms)，重置loading狀態`);
        setLoading(false);
        loadingStartedAtRef.current = null;
      } else {
        // 仍在阈值内，安排定时器在剩余时间内触发恢复
        const remainingTime = STALE_LOADING_THRESHOLD - loadingDuration;
        console.log(`🔄 到期總表頁面正在加載中 (${loadingDuration}ms)，安排${remainingTime}ms後自動恢復`);

        // 清除任何现有的定时器
        if (staleRecoveryTimerRef.current) {
          clearTimeout(staleRecoveryTimerRef.current);
        }

        // 安排自动恢复
        staleRecoveryTimerRef.current = window.setTimeout(async () => {
          console.log('🔄 自動恢復：重置loading狀態並重新加載數據');
          setLoading(false);
          loadingStartedAtRef.current = null;
          staleRecoveryTimerRef.current = null;

          try {
            if (currentCompany) {
              await loadExpiryData(parseInt(currentCompany.id), daysRange);
            } else {
              const company = await companyService.getCurrentCompany();
              if (company) {
                setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
                await loadExpiryData(company.id, daysRange);
              }
            }
          } catch (error) {
            console.error('到期總表頁面自動恢復失敗:', error);
          }
        }, remainingTime);

        return;
      }
    } else if (loading) {
      console.log('🔄 到期總表頁面正在加載中，跳過焦點恢復');
      return;
    }

    try {
      if (currentCompany) {
        console.log('🔄 到期總表頁面焦點恢復，重新加載數據');
              await loadExpiryData(parseInt(currentCompany.id), daysRange);
      } else {
        // 如果没有当前公司，重新初始化
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
          await loadExpiryData(company.id, daysRange);
        }
      }
    } catch (error) {
      console.error('頁面焦點恢復失敗:', error);
    }
  }, [loading, currentCompany, daysRange]);

  // 使用页面焦点恢复 hook
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000,
    enabled: false
  });

  // 初始化 - 获取当前公司，并检查是否有保存的搜索条件
  useEffect(() => {
    const initialize = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });

          // 检查是否有保存的搜索条件（从页面刷新恢复）
          const savedSearch = pickReloadSearch();
          if (savedSearch) {
            console.log('📖 從 reloadSearch 恢復搜索條件:', savedSearch);

            // 恢复搜索条件到状态
            const filters = savedSearch.filters;
            if (filters.daysRange) setDaysRange(filters.daysRange);
            if (filters.productStatusFilter) setProductStatusFilter(filters.productStatusFilter);
            if (filters.inventoryFilter) setInventoryFilter(filters.inventoryFilter);
            if (filters.searchSkuId !== undefined) setSearchSkuId(filters.searchSkuId);
            if (filters.searchBrand !== undefined) setSearchBrand(filters.searchBrand);
            if (filters.searchName !== undefined) setSearchName(filters.searchName);

            // 自动执行搜索（设置loading状态避免空状态闪烁）
            setLoading(true);
            console.log('🔄 自動執行搜索，使用恢復的條件');
            await loadExpiryData(company.id, (filters.daysRange as 'expired' | '30' | '90' | '180') || '180');
          }
        }
      } catch (error) {
        console.error('初始化失败:', error);
        toast.error(language === 'zh' ? '初始化失敗' : 'Initialization failed');
      }
    };

    initialize();
  }, []);

  // 时间范围变化时只更新状态，不自动加载数据
  const handleDaysRangeChange = (newRange: 'expired' | '30' | '90' | '180') => {
    setDaysRange(newRange);
  };

  // 开始搜索处理函数 - 执行真正的页面刷新但保留用户输入
  const handleStartSearch = () => {
    if (!currentCompany) return;

    // 保存用户当前的搜索条件并触发页面刷新
    const searchFilters = {
      daysRange,
      productStatusFilter,
      inventoryFilter,
      searchSkuId,
      searchBrand,
      searchName
    };

    console.log('💾 保存搜索條件並觸發頁面刷新:', searchFilters);
    triggerReloadSearch(searchFilters);
  };

  // 格式化货币
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // 获取状态徽章 - 顯示產品上線狀態
  const getStatusBadge = (status: 'online' | 'offline') => {
    if (status === 'online') {
      return <Badge variant="outline" className="text-xs border-green-500 bg-green-600 text-white">
        <Package className="w-3 h-3 mr-1" />
        {language === 'zh' ? '上線' : 'Online'}
      </Badge>;
    } else {
      return <Badge variant="secondary" className="text-xs bg-gray-500 text-white border-gray-500">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {language === 'zh' ? '下線' : 'Offline'}
      </Badge>;
    }
  };

  // 匯出 Excel：以畫面計算後的內容與排列次序，SKU ID 純文字，天數欄位保留顏色
  const handleExportExcel = () => {
    if (displayProducts.length === 0) {
      toast.error(language === 'zh' ? '沒有資料可匯出' : 'No data to export');
      return;
    }
    const headers = [
      'SKU ID',
      language === 'zh' ? '品牌' : 'Brand',
      language === 'zh' ? '產品名稱' : 'Product Name',
      language === 'zh' ? '數量' : 'Quantity',
      language === 'zh' ? '天數' : 'Days',
      language === 'zh' ? '成本' : 'Cost',
      language === 'zh' ? '狀態' : 'Status'
    ];
    const rows: (string | number)[][] = [headers];
    displayProducts.forEach(p => {
      rows.push([
        p.sku_id,
        p.brand,
        getProductDisplayName(p),
        p.quantity,
        p.days_remaining,
        p.cost,
        p.sku_status === 'online' ? (language === 'zh' ? '上線' : 'Online') : (language === 'zh' ? '下線' : 'Offline')
      ]);
    });
    const worksheet = XLSXStyle.utils.aoa_to_sheet(rows);
    const range = XLSXStyle.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = 1; row <= range.e.r; row++) {
      const cellA = XLSXStyle.utils.encode_cell({ r: row, c: 0 });
      if (worksheet[cellA]) {
        worksheet[cellA].t = 's';
        worksheet[cellA].v = String(worksheet[cellA].v);
      }
      const cellE = XLSXStyle.utils.encode_cell({ r: row, c: 4 });
      if (worksheet[cellE]) {
        const days = Number(worksheet[cellE].v);
        let rgb = 'FF4ADE80';
        if (days < 0) rgb = 'FFDC2626';
        else if (days <= 7) rgb = 'FFF87171';
        else if (days <= 30) rgb = 'FFFB923C';
        worksheet[cellE].s = {
          fill: { patternType: 'solid', fgColor: { rgb } }
        };
      }
    }
    if (!worksheet['!cols']) worksheet['!cols'] = [];
    worksheet['!cols'][0] = { wch: 18 };
    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, language === 'zh' ? '到期產品列表' : 'Expiry List');
    const companyLabel = (currentCompany?.name || currentCompany?.code || 'export').replace(/[/\\?*:[\]]/g, '_');
    const today = new Date().toISOString().split('T')[0];
    const filename = `expiry_summary_${companyLabel}_${today}.xlsx`;
    XLSXStyle.writeFile(workbook, filename);
    toast.success(language === 'zh' ? '已匯出 Excel' : 'Exported to Excel');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                localStorage.setItem('selectedCategory', EXPIRY_CATEGORY);
                navigate(ROUTES.dashboard.root);
              }}
              className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回到期日總表' : 'Back to Expiry Summary'}
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-orange-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {language === 'zh' ? '到期日總表' : 'Expiry Summary'}
              </h1>
            </div>
          </div>

          {/* 頁面導覽按鈕 */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.statistics)}
              className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
              title={language === 'zh' ? '產品統計' : 'Product Statistics'}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <BarChart3 className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-600/20 border border-orange-500/30 rounded-md">
              <Calendar className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">
                {language === 'zh' ? '到期日總表' : 'Expiry Summary'}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.expiry.expiryProcessing)}
              className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
              title={language === 'zh' ? '到期日核對' : 'Expiry Verification'}
            >
              <CheckCircle2 className="w-4 h-4" />
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'zh' ? '已過期' : 'Expired'}
                  </p>
                  <p className="text-2xl font-bold text-red-600">{statistics.expiredProducts}</p>
                </div>
                <Flame className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'zh' ? '7天內到期' : '7 Days'}
                  </p>
                  <p className="text-2xl font-bold text-red-400">{statistics.urgentProducts}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'zh' ? '30天內到期' : '30 Days'}
                  </p>
                  <p className="text-2xl font-bold text-orange-400">{statistics.nearExpiryProducts}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'zh' ? '90天內到期' : '90 Days'}
                  </p>
                  <p className="text-2xl font-bold text-yellow-400">{statistics.mediumExpiryProducts}</p>
                </div>
                <Calendar className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'zh' ? '180天內到期' : '180 Days'}
                  </p>
                  <p className="text-2xl font-bold text-green-400">{statistics.longExpiryProducts}</p>
                </div>
                <Package className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">
              {language === 'zh' ? '篩選條件' : 'Filters'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* 时间范围选择 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '時間範圍' : 'Time Range'}
                </label>
                <Select value={daysRange} onValueChange={handleDaysRangeChange}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="expired" className="text-foreground">{language === 'zh' ? '已過期' : 'Expired'}</SelectItem>
                    <SelectItem value="30" className="text-foreground">30 {language === 'zh' ? '天' : 'Days'}</SelectItem>
                    <SelectItem value="90" className="text-foreground">90 {language === 'zh' ? '天' : 'Days'}</SelectItem>
                    <SelectItem value="180" className="text-foreground">180 {language === 'zh' ? '天' : 'Days'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 狀態過濾 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '狀態' : 'Status'}
                </label>
                <Select value={productStatusFilter} onValueChange={(value: 'all' | 'online' | 'offline') => setProductStatusFilter(value)}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="all" className="text-foreground">{language === 'zh' ? '全部' : 'All'}</SelectItem>
                    <SelectItem value="online" className="text-foreground">Online</SelectItem>
                    <SelectItem value="offline" className="text-foreground">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 存貨量過濾 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '存貨量' : 'Inventory'}
                </label>
                <Select value={inventoryFilter} onValueChange={(value: 'has_stock' | 'zero_stock') => setInventoryFilter(value)}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="has_stock" className="text-foreground">{language === 'zh' ? '有存貨' : 'Has Stock'}</SelectItem>
                    <SelectItem value="zero_stock" className="text-foreground">{language === 'zh' ? '0 庫存' : 'Zero Stock'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SKU ID 搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? 'SKU ID 搜尋' : 'SKU ID Search'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入 SKU ID' : 'Enter SKU ID'}
                    value={searchSkuId}
                    onChange={(e) => setSearchSkuId(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 品牌搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '品牌搜尋' : 'Brand Search'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入品牌名稱' : 'Enter Brand Name'}
                    value={searchBrand}
                    onChange={(e) => setSearchBrand(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 名稱搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '名稱搜尋' : 'Name Search'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入產品名稱' : 'Enter Product Name'}
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 刷新按钮 - 佔用兩個欄位 */}
              <div className="md:col-span-2 lg:col-span-2 flex items-end">
                <Button
                  onClick={handleStartSearch}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (language === 'zh' ? '搜尋中...' : 'Searching...') : (language === 'zh' ? '開始搜尋' : 'Start Search')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-foreground">
              {language === 'zh' ? '到期產品列表' : 'Expiry Products List'}
            </CardTitle>
            <Button
              onClick={handleExportExcel}
              disabled={loading || displayProducts.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              {language === 'zh' ? '匯出 Excel' : 'Export Excel'}
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                <p className="text-muted-foreground mt-2">{language === 'zh' ? '載入中...' : 'Loading...'}</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'zh' ? '沒有找到到期產品' : 'No expiring products found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">SKU ID</TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('brand')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '品牌' : 'Brand'}
                          {sortConfig.key === 'brand' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground text-right">{language === 'zh' ? '數量' : 'Quantity'}</TableHead>
                      <TableHead
                        className="text-muted-foreground text-right cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('days_remaining')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {language === 'zh' ? '天數' : 'Days'}
                          {sortConfig.key === 'days_remaining' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="text-muted-foreground text-right">{language === 'zh' ? '成本' : 'Cost'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '狀態' : 'Status'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayProducts.map((product, index) => {
                      const expiryInfo = getProductExpiryInfo(product);
                      return (
                        <TableRow key={index} className="border-border hover:bg-accent/50">
                          <TableCell className="text-foreground font-mono">{product.sku_id}</TableCell>
                          <TableCell className="text-foreground">{product.brand}</TableCell>
                          <TableCell className="text-foreground">{getProductDisplayName(product)}</TableCell>
                          <TableCell className="text-muted-foreground text-right">{product.quantity.toLocaleString()}</TableCell>
                          <TableCell className="text-muted-foreground text-right">
                            <span className={`font-semibold ${
                              product.days_remaining < 0 ? 'text-red-600' :  // 已過期：鮮紅色
                              product.days_remaining <= 7 ? 'text-red-400' : // 7天內：紅色
                              product.days_remaining <= 30 ? 'text-orange-400' : // 30天內：橙色
                              'text-green-400' // 其他：綠色
                            }`}>
                              {product.days_remaining}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-right">{formatCurrency(product.cost)}</TableCell>
                          <TableCell>{getStatusBadge(product.sku_status)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductExpirySummary;
